import { useEffect } from 'react';

interface UseNavigationGuardOptions {
  /** Fonction qui retourne true s'il y a des modifications non sauvegardées */
  hasUnsavedChanges: () => boolean;
  /** Fonction appelée pour sauvegarder avant de quitter (valeur de retour ignorée) */
  onSave: () => Promise<unknown>;
}

/**
 * Hook pour protéger contre la perte de données lors du rafraîchissement
 * ou de la fermeture de page.
 *
 * Note: La protection contre la navigation React Router nécessiterait
 * l'utilisation de createBrowserRouter. Avec la sauvegarde au blur,
 * les données sont déjà sauvegardées avant la navigation interne.
 */
export function useNavigationGuard({
  hasUnsavedChanges,
  onSave,
}: UseNavigationGuardOptions) {
  // Protection contre le rafraîchissement / fermeture de page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        // Tenter de sauvegarder avant de partir
        onSave().catch(() => {
          // Ignorer les erreurs, le navigateur va quand même partir
        });

        // Afficher la confirmation du navigateur
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, onSave]);
}
